/**
 * UserPopover — profile popover. Available to all users: Account & Setting, the
 * UI language (once UI_I18N_ENABLED) and Switch Agent are open to everyone; the
 * admin-only sections (Backend Management and "View as" impersonation) are gated
 * on admin privileges.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useImpersonation } from '../../context/ImpersonationContext';
import { isValidAlias, normaliseAlias } from '../../context/impersonationStorage';
import { useAgent } from '../../context/AgentContext';
import { MULTI_AGENT_ENABLED, ADMIN_PAGE_LDAP_GROUP, UI_I18N_ENABLED } from '../../config';
import { htmlLangFor } from '../../i18n/locale';
import { LOCALES } from '../../i18n/resources';
import { useLocale } from '../../i18n/useLocale';
import './UserPopover.css';

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ verticalAlign: 'middle', marginRight: 4 }}>
    <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="2" fill="none" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BADGE_PHOTO_URL = 'https://badgephotos.corp.amazon.com/';

interface UserPopoverProps {
  open: boolean;
  onClose: () => void;
}

export default function UserPopover({ open, onClose }: UserPopoverProps) {
  const { t } = useTranslation('shell');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { uid, display_name, ldap_groups } = useAuth();
  const { isImpersonating, isAdmin, exitImpersonation, enterImpersonation } = useImpersonation();
  const { currentAgent, switchAgent, selectableAgents } = useAgent();
  const { locale, legacy, setLocale } = useLocale();
  const [aliasInput, setAliasInput] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    // Delay attaching to avoid immediate close from the click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open, onClose]);

  // Auto-focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  if (!open) return null;

  /* Checked here as well as in the context, so a malformed login is explained rather than
     silently ignored — the context refuses it, and a Go button that does nothing reads as
     broken. */
  const aliasCandidate = normaliseAlias(aliasInput);
  const aliasInvalid = aliasCandidate !== '' && !isValidAlias(aliasCandidate);

  const handleSubmit = () => {
    if (!aliasCandidate || aliasInvalid) return;
    enterImpersonation(aliasCandidate);
    setAliasInput('');
    onClose();
  };

  const handleAccountSettings = () => {
    onClose();
    navigate('/account-settings');
  };

  const handleBackendMgmt = () => {
    onClose();
    navigate('/admin-rbms');
  };

  return (
    <div className="user-popover" ref={popoverRef}>
      {/* Profile header */}
      <div className="user-popover__header">
        <div className="user-popover__profile">
          <img
            src={`${BADGE_PHOTO_URL}?uid=${uid}`}
            className="user-popover__avatar"
            alt={uid}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/icons/user-avatar.svg';
            }}
          />
          <div className="user-popover__info">
            <span className="user-popover__name">{display_name || uid}</span>
            <span className="user-popover__alias">@{uid}</span>
          </div>
        </div>
        <button className="user-popover__close-btn" onClick={onClose} aria-label={t('popover.close')}>
          ×
        </button>
      </div>

      {/* Account & Setting — available to all users */}
      <div className="user-popover__section">
        <button className="user-popover__account-link" onClick={handleAccountSettings}>
          <span className="user-popover__account-label">{t('popover.accountSettings')}</span>
          <ChevronRightIcon />
        </button>
      </div>

      {/* UI language — only once the zh/en switch is on for this stage */}
      {UI_I18N_ENABLED && (
      <div className="user-popover__section">
        <div className="user-popover__section-title">{t('popover.language')}</div>
        {LOCALES.map(l => (
        <label key={l} className="user-popover__radio-item">
          <input
            type="radio"
            name="ui-language"
            value={l}
            checked={!legacy && locale === l}
            onChange={() => { void setLocale(l); }}
          />
          {/* Each language is named in itself, so it can be found whatever the current one is. */}
          <span lang={htmlLangFor(l)}>{tCommon(`language.${l}`)}</span>
        </label>
        ))}
      </div>
      )}

      {/* Backend Management System — admin-only entry */}
      {ldap_groups.includes(ADMIN_PAGE_LDAP_GROUP) && (
      <div className="user-popover__section">
        <button className="user-popover__account-link" onClick={handleBackendMgmt}>
          <span className="user-popover__account-label">{t('popover.backendManagement')}</span>
          <ChevronRightIcon />
        </button>
      </div>
      )}

      {/* Switch Agent */}
      {MULTI_AGENT_ENABLED && (
      <div className="user-popover__section">
        <div className="user-popover__section-title">{t('popover.switchAgent')}</div>
        {selectableAgents.map(agent => (
        <label key={agent.id} className="user-popover__radio-item">
          <input
            type="radio"
            name="agent-switch"
            value={agent.id}
            checked={currentAgent === agent.id}
            onChange={() => { switchAgent(agent.id); onClose(); navigate('/'); }}
          />
          <span>{agent.label}</span>
        </label>
        ))}
      </div>
      )}

      {/* View as Other User — admin only */}
      {isAdmin && (
      <div className="user-popover__section">
        <div className="user-popover__section-title"><EyeIcon /> {t('popover.viewAsOther')}</div>
        <div className="user-popover__input-row">
          <input
            ref={inputRef}
            type="text"
            placeholder={t('popover.aliasPlaceholder')}
            value={aliasInput}
            onChange={(e) => setAliasInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            className="user-popover__alias-input"
          />
          <button
            onClick={handleSubmit}
            className="user-popover__go-btn"
            disabled={!aliasCandidate || aliasInvalid}
          >
            {t('popover.go')}
          </button>
        </div>
        {aliasInvalid && (
          <div className="user-popover__alias-error" role="alert">
            {t('popover.aliasInvalid')}
          </div>
        )}
        {isImpersonating && (
          <button
            onClick={() => { exitImpersonation(); onClose(); }}
            className="user-popover__exit-btn"
          >
            {t('popover.exitViewing')}
          </button>
        )}
      </div>
      )}
    </div>
  );
}
